import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Divider,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface InvoiceItem {
  description: string;
  qty: number;
  unit_price: number;
  tax_percent: number;
}

interface Payment {
  amount: number;
  payment_method: string;
  notes?: string;
  paid_at?: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  client_name: string;
  total_amount: number;
  payment_status: string;
  invoice_date: string;
  items?: InvoiceItem[];
  payments?: Payment[];
  notes?: string;
}

interface ClientOption {
  id: string;
  label: string;
}

const emptyItem: InvoiceItem = { description: '', qty: 1, unit_price: 0, tax_percent: 0 };

const statusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'paid': return 'success';
    case 'partial': return 'warning';
    case 'unpaid':
    case 'pending':
    default: return 'error';
  }
};

const InvoicingPage = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([{ ...emptyItem }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'cash', notes: '' });
  const [payError, setPayError] = useState<string | null>(null);
  const [paySaving, setPaySaving] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.INVOICING.INVOICES);
      setInvoices(res.data || res || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const fetchClients = async () => {
    try {
      const res = await api.get(API_ENDPOINTS.CLIENTS.LIST);
      const clients = res.data || res || [];
      setClientOptions(clients.map((c: { id: string; first_name: string; last_name: string; email: string }) => ({
        id: c.id,
        label: `${c.first_name} ${c.last_name} (${c.email})`,
      })));
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const openCreate = () => {
    setSelectedClient(null);
    setItems([{ ...emptyItem }]);
    setNotes('');
    setError(null);
    setCreateOpen(true);
    fetchClients();
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => setItems((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index));

  const totalAmount = items.reduce((sum, item) => {
    const subtotal = item.qty * item.unit_price;
    return sum + subtotal + (subtotal * item.tax_percent) / 100;
  }, 0);

  const handleCreate = async () => {
    if (!selectedClient) { setError('Please select a client.'); return; }
    if (items.some((i) => !i.description)) { setError('All items must have a description.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(API_ENDPOINTS.INVOICING.CREATE_INVOICE, {
        client_id: selectedClient.id,
        items,
        notes,
        total_amount: totalAmount,
      });
      await fetchInvoices();
      setCreateOpen(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || 'Failed to create invoice.');
    } finally {
      setSaving(false);
    }
  };

  const openView = async (invoice: Invoice) => {
    try {
      const res = await api.get(API_ENDPOINTS.INVOICING.GET_INVOICE(invoice.id));
      setViewInvoice(res.data || res);
    } catch {
      setViewInvoice(invoice);
    }
  };

  const handlePayment = async () => {
    if (!viewInvoice) return;
    if (!paymentForm.amount || isNaN(Number(paymentForm.amount))) {
      setPayError('Enter a valid amount.');
      return;
    }
    setPaySaving(true);
    setPayError(null);
    try {
      await api.post(API_ENDPOINTS.INVOICING.PAYMENT(viewInvoice.id), {
        amount: Number(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes,
      });
      setPaymentOpen(false);
      setPaymentForm({ amount: '', payment_method: 'cash', notes: '' });
      await fetchInvoices();
      const refreshed = await api.get(API_ENDPOINTS.INVOICING.GET_INVOICE(viewInvoice.id));
      setViewInvoice(refreshed.data || refreshed);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setPayError(msg || 'Failed to record payment.');
    } finally {
      setPaySaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Invoicing</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Invoice
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No invoices found</TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell>{inv.invoice_number || `#${inv.id}`}</TableCell>
                  <TableCell>{inv.client_name}</TableCell>
                  <TableCell>{new Date(inv.invoice_date).toLocaleDateString()}</TableCell>
                  <TableCell>${Number(inv.total_amount).toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={inv.payment_status}
                      color={statusColor(inv.payment_status) as 'success' | 'warning' | 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openView(inv)}>
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Invoice Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Invoice</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              value={selectedClient}
              onChange={(_, val) => setSelectedClient(val)}
              renderInput={(params) => <TextField {...params} label="Client *" />}
            />
            <Typography variant="subtitle2">Line Items</Typography>
            {items.map((item, index) => (
              <Box key={index} display="flex" gap={1} alignItems="center">
                <TextField
                  label="Description"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  sx={{ flex: 3 }}
                />
                <TextField
                  label="Qty"
                  type="number"
                  value={item.qty}
                  onChange={(e) => handleItemChange(index, 'qty', Number(e.target.value))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Unit Price"
                  type="number"
                  value={item.unit_price}
                  onChange={(e) => handleItemChange(index, 'unit_price', Number(e.target.value))}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Tax %"
                  type="number"
                  value={item.tax_percent}
                  onChange={(e) => handleItemChange(index, 'tax_percent', Number(e.target.value))}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => removeItem(index)} disabled={items.length === 1} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button variant="outlined" onClick={addItem} size="small" sx={{ alignSelf: 'flex-start' }}>
              + Add Item
            </Button>
            <Divider />
            <Typography variant="body1" fontWeight="bold">Total: ${totalAmount.toFixed(2)}</Typography>
            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={!!viewInvoice} onClose={() => setViewInvoice(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice {viewInvoice?.invoice_number || `#${viewInvoice?.id}`}</DialogTitle>
        <DialogContent>
          {viewInvoice && (
            <Box>
              <Typography><strong>Client:</strong> {viewInvoice.client_name}</Typography>
              <Typography><strong>Date:</strong> {new Date(viewInvoice.invoice_date).toLocaleDateString()}</Typography>
              <Typography><strong>Status:</strong> {viewInvoice.payment_status}</Typography>
              {viewInvoice.notes && <Typography><strong>Notes:</strong> {viewInvoice.notes}</Typography>}
              <Divider sx={{ my: 2 }} />
              {viewInvoice.items && viewInvoice.items.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Items</Typography>
                  {viewInvoice.items.map((item, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" py={0.5}>
                      <Typography variant="body2">{item.description} x{item.qty}</Typography>
                      <Typography variant="body2">${(item.qty * item.unit_price).toFixed(2)}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <Typography fontWeight="bold">Total: ${Number(viewInvoice.total_amount).toFixed(2)}</Typography>
              {viewInvoice.payments && viewInvoice.payments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Payment History</Typography>
                  {viewInvoice.payments.map((p, i) => (
                    <Box key={i} display="flex" justifyContent="space-between">
                      <Typography variant="body2">{p.payment_method} — {p.notes}</Typography>
                      <Typography variant="body2">${Number(p.amount).toFixed(2)}</Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewInvoice(null)}>Close</Button>
          {viewInvoice?.payment_status !== 'paid' && (
            <Button
              variant="contained"
              color="success"
              onClick={() => { setPayError(null); setPaymentOpen(true); }}
            >
              Record Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {payError && <Alert severity="error" sx={{ mb: 2 }}>{payError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
            />
            <TextField
              select
              label="Payment Method"
              fullWidth
              value={paymentForm.payment_method}
              onChange={(e) => setPaymentForm((p) => ({ ...p, payment_method: e.target.value }))}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="eftpos">EFTPOS</MenuItem>
            </TextField>
            <TextField
              label="Notes"
              fullWidth
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)} disabled={paySaving}>Cancel</Button>
          <Button variant="contained" onClick={handlePayment} disabled={paySaving}>
            {paySaving ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoicingPage;
